import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/apiconfig';
import { ui } from "../config/theme";

import { Eye, Star, FileText, Calendar, User, Building, MapPin, Clock, Award, MessageSquare, Plus } from 'lucide-react';
import {
    BriefcaseIcon,
    CalendarIcon,
    StarIcon,
    ClipboardDocumentListIcon,
    CheckCircleIcon,
    ClockIcon,
    XCircleIcon,
    EyeIcon
} from '@heroicons/react/24/outline';
import Header from "../components/Header";
import Footer from "../components/Footer";

const MyApplications = () => {
    const { user } = useAuth();
    const [applications, setApplications] = useState([]);
    const [assessments, setAssessments] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewData, setReviewData] = useState({
        overall_rating: 5,
        communication_rating: 5,
        professionalism_rating: 5,
        process_efficiency_rating: 5,
        company_culture_rating: 5,
        interview_experience_rating: 5,
        review_title: '',
        review_text: '',
        pros: '',
        cons: '',
        would_recommend: 'Yes',
        interview_difficulty: 'Average',
        is_anonymous: false
    });
    const [submittingReview, setSubmittingReview] = useState(false);
    const [reviewedApplications, setReviewedApplications] = useState(new Set());

    useEffect(() => {
        if (user) {
            fetchApplications();
            fetchAssessments();
            fetchReviews();
        }
    }, [user]);

    // Re-check reviewed applications whenever the applications list is loaded/updated
    useEffect(() => {
        if (user && applications && applications.length > 0) {
            checkReviewedApplications();
        }
    }, [user, applications]);

    const fetchApplications = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/applications/candidate/${user.email}`);
            if (response.ok) {
                const data = await response.json();
                setApplications(data);
            }
        } catch (error) {
            console.error('Error fetching applications:', error);
            setError('Failed to load applications');
        } finally {
            setLoading(false);
        }
    };

    const fetchAssessments = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/assessments/candidate/${user.id}`);
            if (response.ok) {
                const data = await response.json();
                setAssessments(data);
            }
        } catch (error) {
            console.error('Error fetching assessments:', error);
        }
    };

    const fetchReviews = async () => {
        try {
            // Load recruiter-given reviews about the candidate
            const response = await fetch(`${API_BASE_URL}/api/assessments/reviews/candidate/${user.id}`);
            if (response.ok) {
                const data = await response.json();
                // This endpoint returns an array of reviews
                setReviews(Array.isArray(data) ? data : (Array.isArray(data?.reviews) ? data.reviews : []));
            }
        } catch (error) {
            console.error('Error fetching reviews:', error);
        }
    };

    const checkReviewedApplications = async () => {
        try {
            if (!applications || applications.length === 0) return;

            const reviewChecks = await Promise.all(
                applications.map(async (app) => {
                    try {
                        const resp = await fetch(
                            `${API_BASE_URL}/api/recruiter-reviews/check/${app.id}/${user.id}`
                        );
                        const reviewData = await resp.json();
                        return { applicationId: app.id, hasReviewed: !!reviewData.hasReviewed };
                    } catch (e) {
                        console.error('Review check failed for application', app.id, e);
                        return { applicationId: app.id, hasReviewed: false };
                    }
                })
            );

            const reviewedSet = new Set(
                reviewChecks
                    .filter(check => check.hasReviewed)
                    .map(check => check.applicationId)
            );
            setReviewedApplications(reviewedSet);
        } catch (error) {
            console.error('Error checking reviewed applications:', error);
        }
    };

    const handleReviewSubmit = async (application) => {
        setSelectedApplication(application);
        setShowReviewModal(true);
        setReviewData({
            overall_rating: 5,
            communication_rating: 5,
            professionalism_rating: 5,
            process_efficiency_rating: 5,
            company_culture_rating: 5,
            interview_experience_rating: 5,
            review_title: '',
            review_text: '',
            pros: '',
            cons: '',
            would_recommend: 'Yes',
            interview_difficulty: 'Average',
            is_anonymous: false
        });
    };

    const submitReview = async () => {
        setSubmittingReview(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/recruiter-reviews`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...reviewData,
                    application_id: selectedApplication.id,
                    candidate_id: user.id,
                    recruiter_id: selectedApplication.recruiter_id,
                    company_id: selectedApplication.company_id,
                    job_posting_id: selectedApplication.job_posting_id
                })
            });

            const data = await response.json();
            
            if (response.ok && data.success) {
                setShowReviewModal(false);
                setReviewedApplications(prev => new Set([...prev, selectedApplication.id]));
                // refresh reviews so it shows under the card immediately
                fetchReviews();
                alert('Review submitted successfully!');
            } else {
                const msg = data.message || 'Failed to submit review';
                // If backend indicates duplicate, mark as reviewed to hide the button
                if (msg.toLowerCase().includes('already reviewed')) {
                    setReviewedApplications(prev => new Set([...prev, selectedApplication.id]));
                    setShowReviewModal(false);
                }
                alert(msg);
            }
        } catch (error) {
            console.error('Error submitting review:', error);
            alert('Failed to submit review');
        } finally {
            setSubmittingReview(false);
        }
    };

    const renderStarRating = (rating, onChange) => {
        return (
            <div className="flex space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                    <button
                        key={star}
                        type="button"
                        onClick={() => onChange(star)}
                        className={`text-xl ${
                            star <= rating ? 'text-yellow-400' : 'text-gray-300'
                        } hover:text-yellow-400 transition-colors`}
                    >
                        ★
                    </button>
                ))}
            </div>
        );
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Hired':
                return 'bg-green-100 text-green-800';
            case 'Rejected':
                return 'bg-red-100 text-red-800';
            case 'Shortlisted':
                return 'bg-blue-100 text-blue-800';
            case 'Offer':
                return 'bg-purple-100 text-purple-800';
            case 'Interviews':
                return 'bg-yellow-100 text-yellow-800';
            case 'Qualified':
                return 'bg-green-100 text-green-800';
            case 'Screening':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getAssessmentStatusIcon = (status) => {
        switch (status) {
            case 'Completed':
                return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
            case 'In Progress':
                return <ClockIcon className="w-5 h-5 text-yellow-500" />;
            case 'Cancelled':
                return <XCircleIcon className="w-5 h-5 text-red-500" />;
            default:
                return <ClockIcon className="w-5 h-5 text-gray-500" />;
        }
    };

    const getApplicationAssessments = (applicationId) => {
        return assessments.filter(assessment => assessment.application_id === applicationId);
    };

    const getApplicationReviews = (applicationId) => {
        return reviews.filter(review => review.application_id === applicationId);
    };

    const handleViewDetails = (application) => {
        setSelectedApplication(application);
        setShowDetailsModal(true);
    };

    if (loading) {
        return (
            <div className="flex flex-col min-h-screen">
                <Header />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading your applications...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className={ui.page}>
            <Header />
            <div className="flex-1 p-6">
                <main className="flex-grow">
                    <div className={ui.container}>
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-900">My Job Applications</h1>
                        <p className="text-gray-600 mt-2">
                            Track your job applications, assessment scores, and feedback from recruiters
                        </p>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center">
                                <BriefcaseIcon className="w-8 h-8 text-blue-600" />
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-500">Total Applications</p>
                                    <p className="text-2xl font-bold text-gray-900">{applications.length}</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center">
                                <ClipboardDocumentListIcon className="w-8 h-8 text-green-600" />
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-500">Assessments</p>
                                    <p className="text-2xl font-bold text-gray-900">{assessments.length}</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center">
                                <StarIcon className="w-8 h-8 text-yellow-600" />
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-500">Reviews</p>
                                    <p className="text-2xl font-bold text-gray-900">{reviews.length}</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center">
                                <CheckCircleIcon className="w-8 h-8 text-purple-600" />
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-500">Avg. Score</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {assessments.length > 0 
                                            ? Math.round(assessments.reduce((acc, a) => acc + (a.percentage || 0), 0) / assessments.length)
                                            : 0}%
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Applications List */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">Applications</h2>
                        </div>
                        
                        {applications.length === 0 ? (
                            <div className="p-8 text-center">
                                <BriefcaseIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500">You haven't applied to any jobs yet.</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-200">
                                {applications.map((application) => {
                                    const appAssessments = getApplicationAssessments(application.id);
                                    const appReviews = getApplicationReviews(application.id);
                                    const avgScore = appAssessments.length > 0 
                                        ? Math.round(appAssessments.reduce((acc, a) => acc + (a.percentage || 0), 0) / appAssessments.length)
                                        : null;

                                    return (
                                        <div key={application.id} className="p-6 hover:bg-gray-50">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h3 className="text-lg font-semibold text-gray-900">
                                                            {application.job_title || 'Job Title Not Available'}
                                                        </h3>
                                                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(application.status)}`}>
                                                            {application.status}
                                                        </span>
                                                    </div>
                                                    
                                                    <div className="flex items-center text-sm text-gray-500 mb-3">
                                                        <CalendarIcon className="w-4 h-4 mr-1" />
                                                        Applied on {new Date(application.application_date).toLocaleDateString()}
                                                    </div>

                                                    {/* Assessment Summary */}
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                                        <div className="bg-gray-50 rounded-lg p-3">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-medium text-gray-700">Assessments</span>
                                                                <span className="text-sm text-gray-600">{appAssessments.length}</span>
                                                            </div>
                                                            {appAssessments.length > 0 && (
                                                                <div className="mt-2">
                                                                    <div className="flex items-center space-x-2">
                                                                        {appAssessments.slice(0, 3).map((assessment, idx) => (
                                                                            <div key={idx} className="flex items-center">
                                                                                {getAssessmentStatusIcon(assessment.status)}
                                                                            </div>
                                                                        ))}
                                                                        {appAssessments.length > 3 && (
                                                                            <span className="text-xs text-gray-500">+{appAssessments.length - 3} more</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        
                                                        <div className="bg-gray-50 rounded-lg p-3">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-medium text-gray-700">Average Score</span>
                                                                <span className="text-sm font-semibold text-gray-900">
                                                                    {avgScore !== null ? `${avgScore}%` : 'N/A'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="bg-gray-50 rounded-lg p-3">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-medium text-gray-700">Reviews</span>
                                                                <span className="text-sm text-gray-600">{appReviews.length}</span>
                                                            </div>
                                                            {appReviews.length > 0 && (
                                                                <div className="mt-1">
                                                                    <div className="flex">
                                                                        {[1, 2, 3, 4, 5].map((star) => (
                                                                            <StarIcon
                                                                                key={star}
                                                                                className={`w-3 h-3 ${
                                                                                    star <= Math.round(appReviews.reduce((acc, r) => acc + r.rating, 0) / appReviews.length)
                                                                                        ? 'text-yellow-500 fill-current' 
                                                                                        : 'text-gray-300'
                                                                                }`}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Recruiter Review (if any) */}
                                                    {appReviews.length > 0 && (
                                                        <div className="mt-4 bg-gray-50 rounded-lg p-4">
                                                            <div className="flex items-center justify-between mb-1">
                                                                <span className="text-sm font-medium text-gray-700">Recruiter Review</span>
                                                                <span className="text-xs text-gray-500">{new Date(appReviews[0].created_at).toLocaleDateString()}</span>
                                                            </div>
                                                            <div className="flex items-center mb-2">
                                                                <span className="mr-2 text-sm text-gray-600">{appReviews[0].review_type || 'Overall'}</span>
                                                                <div className="flex">
                                                                    {[1,2,3,4,5].map(star => (
                                                                        <StarIcon key={star} className={`w-4 h-4 ${star <= (appReviews[0].rating || 0) ? 'text-yellow-500 fill-current' : 'text-gray-300'}`} />
                                                                    ))}
                                                                </div>
                                                            </div>
                                                            {appReviews[0].recommendation && (
                                                                <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                                                                    appReviews[0].recommendation === 'Strongly Recommend' || appReviews[0].recommendation === 'Recommend'
                                                                        ? 'bg-green-100 text-green-800'
                                                                        : appReviews[0].recommendation === 'Neutral'
                                                                        ? 'bg-yellow-100 text-yellow-800'
                                                                        : 'bg-red-100 text-red-800'
                                                                }`}>
                                                                    {appReviews[0].recommendation}
                                                                </span>
                                                            )}
                                                            {appReviews[0].feedback && (
                                                                <p className="text-sm text-gray-700 mt-2"><span className="font-medium">Feedback: </span>{appReviews[0].feedback}</p>
                                                            )}
                                                            {appReviews[0].strengths && (
                                                                <p className="text-sm text-gray-700 mt-1"><span className="font-medium">Strengths: </span>{appReviews[0].strengths}</p>
                                                            )}
                                                            {appReviews[0].areas_for_improvement && (
                                                                <p className="text-sm text-gray-700 mt-1"><span className="font-medium">Areas for Improvement: </span>{appReviews[0].areas_for_improvement}</p>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                <div className="ml-6">
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() => handleViewDetails(application)}
                                                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200"
                                                        >
                                                            <Eye className="w-4 h-4 mr-1" />
                                                            View Details
                                                        </button>
                                                        {!reviewedApplications.has(application.id) && (
                                                            <button
                                                                onClick={() => handleReviewSubmit(application)}
                                                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-green-600 bg-green-100 hover:bg-green-200"
                                                            >
                                                                <Plus className="w-4 h-4 mr-1" />
                                                                Review
                                                            </button>
                                                        )}
                                                        {reviewedApplications.has(application.id) && (
                                                            <span className="inline-flex items-center px-3 py-1 text-sm font-medium text-gray-500 bg-gray-100 rounded-md">
                                                                <MessageSquare className="w-4 h-4 mr-1" />
                                                                Reviewed
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    </div>

                </main>
            </div>

            {/* Review Modal */}
            {showReviewModal && selectedApplication && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900">
                                    Review Your Experience
                                </h3>
                                <button
                                    onClick={() => setShowReviewModal(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    ✕
                                </button>
                            </div>
                            
                            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                                <h4 className="font-medium text-gray-900">{selectedApplication.job_title}</h4>
                                <p className="text-sm text-gray-600">{selectedApplication.company_name}</p>
                            </div>

                            <div className="space-y-6 max-h-96 overflow-y-auto">
                                {/* Rating Section */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Overall Rating
                                        </label>
                                        {renderStarRating(reviewData.overall_rating, (rating) => 
                                            setReviewData({...reviewData, overall_rating: rating})
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Communication
                                        </label>
                                        {renderStarRating(reviewData.communication_rating, (rating) => 
                                            setReviewData({...reviewData, communication_rating: rating})
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Professionalism
                                        </label>
                                        {renderStarRating(reviewData.professionalism_rating, (rating) => 
                                            setReviewData({...reviewData, professionalism_rating: rating})
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Process Efficiency
                                        </label>
                                        {renderStarRating(reviewData.process_efficiency_rating, (rating) => 
                                            setReviewData({...reviewData, process_efficiency_rating: rating})
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Company Culture
                                        </label>
                                        {renderStarRating(reviewData.company_culture_rating, (rating) => 
                                            setReviewData({...reviewData, company_culture_rating: rating})
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Interview Experience
                                        </label>
                                        {renderStarRating(reviewData.interview_experience_rating, (rating) => 
                                            setReviewData({...reviewData, interview_experience_rating: rating})
                                        )}
                                    </div>
                                </div>

                                {/* Review Details */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Review Title
                                    </label>
                                    <input
                                        type="text"
                                        value={reviewData.review_title}
                                        onChange={(e) => setReviewData({...reviewData, review_title: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Brief title for your review"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Review Text
                                    </label>
                                    <textarea
                                        value={reviewData.review_text}
                                        onChange={(e) => setReviewData({...reviewData, review_text: e.target.value})}
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Share your experience with this recruiter and company"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Pros
                                        </label>
                                        <textarea
                                            value={reviewData.pros}
                                            onChange={(e) => setReviewData({...reviewData, pros: e.target.value})}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="What went well?"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Cons
                                        </label>
                                        <textarea
                                            value={reviewData.cons}
                                            onChange={(e) => setReviewData({...reviewData, cons: e.target.value})}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="What could be improved?"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Would you recommend this recruiter?
                                        </label>
                                        <select
                                            value={reviewData.would_recommend}
                                            onChange={(e) => setReviewData({...reviewData, would_recommend: e.target.value})}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="Yes">Yes</option>
                                            <option value="Maybe">Maybe</option>
                                            <option value="No">No</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Interview Difficulty
                                        </label>
                                        <select
                                            value={reviewData.interview_difficulty}
                                            onChange={(e) => setReviewData({...reviewData, interview_difficulty: e.target.value})}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="Very Easy">Very Easy</option>
                                            <option value="Easy">Easy</option>
                                            <option value="Average">Average</option>
                                            <option value="Difficult">Difficult</option>
                                            <option value="Very Difficult">Very Difficult</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="anonymous"
                                        checked={reviewData.is_anonymous}
                                        onChange={(e) => setReviewData({...reviewData, is_anonymous: e.target.checked})}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="anonymous" className="ml-2 block text-sm text-gray-700">
                                        Submit this review anonymously
                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                                <button
                                    onClick={() => setShowReviewModal(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={submitReview}
                                    disabled={submittingReview}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {showDetailsModal && selectedApplication && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-semibold">
                                Application Details - {selectedApplication.job_title}
                            </h3>
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <XCircleIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Assessments */}
                            <div>
                                <h4 className="text-lg font-semibold mb-4 flex items-center">
                                    <ClipboardDocumentListIcon className="w-5 h-5 mr-2" />
                                    Assessments
                                </h4>
                                <div className="space-y-4">
                                    {getApplicationAssessments(selectedApplication.id).map((assessment) => (
                                        <div key={assessment.id} className="border rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center">
                                                    {getAssessmentStatusIcon(assessment.status)}
                                                    <span className="ml-2 font-medium">{assessment.assessment_type}</span>
                                                </div>
                                                <span className="text-sm text-gray-500">
                                                    {new Date(assessment.assessment_date).toLocaleDateString()}
                                                </span>
                                            </div>
                                            
                                            <div className="grid grid-cols-2 gap-4 mb-2">
                                                <div>
                                                    <span className="text-sm text-gray-500">Score:</span>
                                                    <span className="ml-2 font-semibold">
                                                        {assessment.score ? `${assessment.score}/${assessment.max_score}` : 'Not scored'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-sm text-gray-500">Percentage:</span>
                                                    <span className="ml-2 font-semibold">
                                                        {assessment.percentage ? `${Math.round(assessment.percentage)}%` : 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            {assessment.notes && (
                                                <div className="mt-2">
                                                    <span className="text-sm text-gray-500">Notes:</span>
                                                    <p className="text-sm text-gray-700 mt-1">{assessment.notes}</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    
                                    {getApplicationAssessments(selectedApplication.id).length === 0 && (
                                        <p className="text-gray-500 text-center py-4">No assessments yet</p>
                                    )}
                                </div>
                            </div>

                            {/* Reviews */}
                            <div>
                                <h4 className="text-lg font-semibold mb-4 flex items-center">
                                    <StarIcon className="w-5 h-5 mr-2" />
                                    Reviews
                                </h4>
                                <div className="space-y-4">
                                    {getApplicationReviews(selectedApplication.id).map((review) => (
                                        <div key={review.id} className="border rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="font-medium">{review.review_type}</span>
                                                <div className="flex items-center">
                                                    <div className="flex mr-2">
                                                        {[1, 2, 3, 4, 5].map((star) => (
                                                            <StarIcon
                                                                key={star}
                                                                className={`w-4 h-4 ${
                                                                    star <= review.rating ? 'text-yellow-500 fill-current' : 'text-gray-300'
                                                                }`}
                                                            />
                                                        ))}
                                                    </div>
                                                    <span className="text-sm text-gray-500">
                                                        {new Date(review.created_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            {review.recommendation && (
                                                <div className="mb-2">
                                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                        review.recommendation.includes('Recommend') ? 'bg-green-100 text-green-800' :
                                                        review.recommendation === 'Neutral' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-red-100 text-red-800'
                                                    }`}>
                                                        {review.recommendation}
                                                    </span>
                                                </div>
                                            )}
                                            
                                            {review.feedback && (
                                                <div className="mb-2">
                                                    <span className="text-sm font-medium text-gray-700">Feedback:</span>
                                                    <p className="text-sm text-gray-600 mt-1">{review.feedback}</p>
                                                </div>
                                            )}
                                            
                                            {review.strengths && (
                                                <div className="mb-2">
                                                    <span className="text-sm font-medium text-gray-700">Strengths:</span>
                                                    <p className="text-sm text-gray-600 mt-1">{review.strengths}</p>
                                                </div>
                                            )}
                                            
                                            {review.areas_for_improvement && (
                                                <div>
                                                    <span className="text-sm font-medium text-gray-700">Areas for Improvement:</span>
                                                    <p className="text-sm text-gray-600 mt-1">{review.areas_for_improvement}</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    
                                    {getApplicationReviews(selectedApplication.id).length === 0 && (
                                        <p className="text-gray-500 text-center py-4">No reviews yet</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default MyApplications;
